import { useEffect, useCallback } from 'react';

interface CancelOrderModalProps {
  isOpen: boolean;
  orderNumber: number;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

export function CancelOrderModal({ isOpen, orderNumber, onConfirm, onCancel, loading }: CancelOrderModalProps) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !loading) onCancel();
  }, [loading, onCancel]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={loading ? undefined : onCancel}
    >
      <div
        className="w-full max-w-[420px] rounded-xl bg-white p-6 text-center shadow-xl animate-[slideUp_200ms_ease-out] sm:p-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Warning icon */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <h2 className="mb-3 text-xl font-semibold text-gray-800">
          Cancelar pedido #{orderNumber}
        </h2>

        <p className="mb-2 text-[15px] text-gray-500">
          ¿Confirmás la cancelación de este pedido?
        </p>

        <p className="mb-6 rounded-md bg-gray-50 p-3 text-[13px] text-gray-500">
          Nota: Si abonaste con tarjeta o MercadoPago procederemos a reembolsar tus fondos.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row-reverse">
          <button
            type="button"
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 text-[15px] font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="h-[18px] w-[18px] animate-spin" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                  <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Cancelando...
              </>
            ) : (
              'Sí, cancelar pedido'
            )}
          </button>
          <button
            type="button"
            className="inline-flex h-11 flex-1 items-center justify-center rounded-lg border border-gray-300 bg-gray-50 text-[15px] font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-70"
            onClick={onCancel}
            disabled={loading}
          >
            No, mantener pedido
          </button>
        </div>
      </div>
    </div>
  );
}
